// hooks/useStories.ts
//
// Stories board data hooks. Mirrors `usePosts` + `useStageBasedPosts`, but:
//   - fetches `getClientPostsWithFilters(clientId, { postType: "story" })`
//     (story posts share the posts table but are scoped by post_type)
//   - filters/labels stages via `storiesStageConfig` (trimmed set)
//   - defaults the active stage to "draft"
//
// Kept separate from `usePosts` (rather than parameterised) because the two
// boards diverge on stage taxonomy and cache key; sharing would tangle the
// story-exclusion logic that keeps stories OFF the Posts board.
import { useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { postsService } from "../services/postsService";
import { Post } from "@/core/types/postTypes";
import { PostItem, ValidStatuses } from "@/core/Post/PostItem.model";
import { useAppStore } from "@/core/store/useAppStore";
import { UserRole } from "@/services/urlService";
import {
  STORIES_STAGE_CONFIGS,
  StoryStage,
  getStoryStageConfig,
  compareStoriesForBoard,
} from "../storiesStageConfig";

// Dedicated cache key so the Stories query never collides with the Posts
// board query (which caches the unfiltered list under postKeys.all).
const storyKeys = {
  all: (clientId: string) => ["stories", clientId] as const,
};

interface UseStoriesOptions {
  stage?: StoryStage;
}

const convertPostItemsToLegacyPosts = (postItems: PostItem[]): Post[] =>
  postItems.map((item) => item.toLegacyPost());

/**
 * Fetches story posts for a client via React Query.
 */
function useStoriesQuery(clientId: string | undefined) {
  return useQuery({
    queryKey: clientId ? storyKeys.all(clientId) : ["stories", "none"],
    queryFn: async () => {
      if (!clientId) return [];
      const posts = await postsService.getClientPostsWithFilters(clientId, {
        postType: "story",
      });
      return posts.map((p) =>
        PostItem.fromLegacyPost(p, UserRole.ADMIN, clientId),
      );
    },
    enabled: !!clientId,
  });
}

/**
 * Primary hook for story posts — filters + sorts by trimmed stage config.
 */
export const useStories = (options: UseStoriesOptions = {}) => {
  const selectedClient = useAppStore((state) => state.selectedClient);
  const queryClient = useQueryClient();

  const {
    data: postItems = [],
    isLoading,
    error,
  } = useStoriesQuery(selectedClient?.id);

  const posts = useMemo(() => {
    const stageStatuses = options.stage
      ? getStoryStageConfig(options.stage).statuses
      : null;

    const filteredPostItems = stageStatuses
      ? postItems.filter((item) =>
          stageStatuses.includes(item.status as ValidStatuses),
        )
      : postItems;

    const sortedPostItems = [...filteredPostItems].sort(compareStoriesForBoard);

    return convertPostItemsToLegacyPosts(sortedPostItems);
  }, [postItems, options.stage]);

  // Stage counts come from the UNFILTERED list so every tab's badge stays
  // stable when the active tab changes.
  const stageCounts = useMemo(
    () =>
      STORIES_STAGE_CONFIGS.reduce(
        (acc, stage) => {
          acc[stage.id] = postItems.filter((item) =>
            stage.statuses.includes(item.status as ValidStatuses),
          ).length;
          return acc;
        },
        {} as Record<StoryStage, number>,
      ),
    [postItems],
  );

  const refetch = useCallback(() => {
    if (!selectedClient?.id) return;
    queryClient.invalidateQueries({
      queryKey: storyKeys.all(selectedClient.id),
    });
  }, [selectedClient?.id, queryClient]);

  return {
    posts,
    isLoading,
    error: error
      ? error instanceof Error
        ? error.message
        : "Failed to load stories"
      : null,
    refetch,
    totalPosts: posts.length,
    stageCounts,
  };
};

/**
 * Stage-aware wrapper — mirrors `useStageBasedPosts`, default stage "draft".
 */
export const useStageBasedStories = () => {
  const [activeStage, setActiveStage] = useState<StoryStage | null>("draft");

  const storiesData = useStories({
    stage: activeStage || undefined,
  });

  return {
    ...storiesData,
    activeStage,
    setActiveStage,
  };
};
